import React, { useMemo, useState } from 'react';

import { reportSurfaces } from '~/configs/unifiedAnalyticsTheme';

import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { inventoryCsvBlob } from './csv';
import { type Row, format, number, text } from './inventoryValues';
import { inventoryColumnKeys, inventoryHierarchy } from './model';
import { inventoryBand } from './prototypeStyle';

// Same native table interaction as ReportTable, with a chunked export for inventory evidence.
export default function InventoryTable({
  title,
  rows,
  columns,
  source,
  truncated = false,
  onSelect,
  selected = [],
  exportColumns,
  exportContext = {},
  exportReady,
  dataBars = false,
  pageSize = 25,
  groupColumns = [],
  hierarchyRows,
}: {
  title: string;
  rows: Array<Row>;
  columns: Array<{ key: string; label: string; numeric?: boolean; percent?: boolean }>;
  source: string;
  truncated?: boolean;
  onSelect?: (row: Row, additive: boolean) => void;
  selected?: Array<string>;
  exportColumns?: Array<string>;
  exportContext?: Record<string, unknown>;
  exportReady: boolean;
  dataBars?: boolean;
  pageSize?: number;
  groupColumns?: Array<string>;
  hierarchyRows?: Array<Row>;
}) {
  const surfaces = reportSurfaces(useTheme());
  const [page, setPage] = useState(0),
    [sort, setSort] = useState(''),
    [ascending, setAscending] = useState(true),
    [expandedVendors, setExpandedVendors] = useState<Array<string>>([]);
  const maxima = useMemo(
    () =>
      Object.fromEntries(
        columns
          .filter((c) => dataBars && c.numeric)
          .map((c) => [c.key, rows.reduce((maximum, row) => Math.max(maximum, Math.abs(number(row[c.key]) ?? 0)), 1)]),
      ),
    [rows, columns, dataBars],
  );
  const sorted = sort
    ? [...rows].sort((a, b) => {
        const an = number(a[sort]),
          bn = number(b[sort]);
        const cmp =
          columns.find((c) => c.key === sort)?.numeric && an !== null && bn !== null
            ? an - bn
            : text(a[sort]).localeCompare(text(b[sort]));
        return ascending ? cmp : -cmp;
      })
    : rows;
  const safePage = Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1));
  const pageRows = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const displayRows = inventoryHierarchy(pageRows, hierarchyRows ?? [], expandedVendors);
  const repeats = (index: number, key: string) => {
    const depth = groupColumns.indexOf(key);
    return (
      depth >= 0 &&
      index > 0 &&
      groupColumns
        .slice(0, depth + 1)
        .every((field) => text(pageRows[index][field]) === text(pageRows[index - 1][field]))
    );
  };
  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={0.5}
        sx={{ ...inventoryBand, '& .MuiButton-root': { color: '#fff' } }}
      >
        <Typography component="h2" variant="h6" sx={{ fontSize: 12, lineHeight: 1.2, fontWeight: 600, flex: 1 }}>
          {title}
        </Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ '& .MuiButton-root': { fontSize: 12, whiteSpace: 'nowrap' } }}>
          {hierarchyRows && (
            <Button
              disabled={!exportReady || truncated}
              onClick={() => {
                if (!exportReady || truncated) return;
                const url = URL.createObjectURL(
                  inventoryCsvBlob(hierarchyRows, inventoryColumnKeys(hierarchyRows), {
                    ...exportContext,
                    dataset: 'vendor_rooms',
                    selection_applies_to_hierarchy: false,
                  }),
                );
                const link = document.createElement('a');
                link.href = url;
                link.download = 'vendor-room-types.csv';
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              Export room types CSV
            </Button>
          )}
          <Button
            disabled={!exportReady || truncated}
            onClick={() => {
              if (!exportReady || truncated) return;
              const url = URL.createObjectURL(
                inventoryCsvBlob(sorted, exportColumns ?? columns.map((c) => c.key), {
                  ...exportContext,
                  ...(hierarchyRows ? { dataset: 'vendors', selection_applies_to_hierarchy: false } : {}),
                }),
              );
              const link = document.createElement('a');
              link.href = url;
              link.download = title.toLowerCase().replace(/\W+/g, '-') + '.csv';
              link.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>
      {truncated && (
        <Alert severity="warning">
          The query reached its row limit. Narrow the filters or dates before exporting the complete scope.
        </Alert>
      )}
      {rows.length === 0 && <Alert severity="info">No rows match these filters.</Alert>}
      {hierarchyRows && (
        <Typography variant="caption" title={source} sx={{ display: 'block', px: 0.75, py: 0.5, fontSize: 10 }}>
          Use + to expand room types. Select a vendor name to filter the chart.
        </Typography>
      )}
      <TableContainer sx={hierarchyRows ? { maxHeight: { xs: 300, md: 400 } } : undefined}>
        <Table
          size="small"
          aria-label={title}
          sx={{
            '& .MuiTableCell-root': { fontSize: 11, px: 0.75, py: 0.5, lineHeight: 1.35, borderColor: surfaces.rule },
            // The LE theme gives head cells white text for its dark header bar. This table keeps the
            // prototype's light header, so the titles and sort labels need the theme's text colour.
            '& .MuiTableHead-root .MuiTableCell-root': {
              bgcolor: surfaces.stripe,
              color: 'text.primary',
              fontWeight: 600,
            },
            '& .MuiTableHead-root .MuiTableSortLabel-root': {
              '&, &:hover, &:focus, &.Mui-active, &.Mui-active .MuiTableSortLabel-icon': { color: 'text.primary' },
            },
            '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': { bgcolor: surfaces.stripe },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((c, ci) => (
                <TableCell
                  key={c.key}
                  align={c.numeric ? 'right' : 'left'}
                  sortDirection={sort === c.key ? (ascending ? 'asc' : 'desc') : false}
                  sx={{ whiteSpace: 'nowrap', ...(hierarchyRows && ci === 0 ? { width: 220, minWidth: 220 } : {}) }}
                >
                  <TableSortLabel
                    active={sort === c.key}
                    direction={ascending ? 'asc' : 'desc'}
                    onClick={() => {
                      setAscending(sort === c.key ? !ascending : true);
                      setSort(c.key);
                    }}
                  >
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map(({ row: r, depth, vendor }, i) => (
              <TableRow key={i} data-hierarchy-depth={depth} hover selected={selected.includes(text(r.vendor_name))}>
                <>
                  {columns.map((c, ci) => (
                    <TableCell
                      key={c.key}
                      align={c.numeric ? 'right' : 'left'}
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: hierarchyRows && ci === 0 ? 220 : c.numeric ? 64 : 120,
                        ...(hierarchyRows && ci === 0 ? { width: 220 } : {}),
                      }}
                    >
                      {ci === 0 && depth === 1 ? (
                        <Box sx={{ pl: 4, whiteSpace: 'nowrap' }}>{text(r.room_type_name)}</Box>
                      ) : repeats(i, c.key) ? null : ci === 0 && onSelect ? (
                        <Stack direction="row" alignItems="center">
                          {hierarchyRows && (
                            <Button
                              size="small"
                              disabled={!exportReady}
                              sx={{ minWidth: 24, flexShrink: 0 }}
                              aria-label={`${expandedVendors.includes(vendor) ? 'Collapse' : 'Expand'} room types for ${vendor}`}
                              aria-expanded={expandedVendors.includes(vendor)}
                              onClick={() =>
                                setExpandedVendors((current) =>
                                  current.includes(vendor)
                                    ? current.filter((name) => name !== vendor)
                                    : [...current, vendor],
                                )
                              }
                            >
                              {expandedVendors.includes(vendor) ? '−' : '+'}
                            </Button>
                          )}
                          <Button
                            size="small"
                            title={text(r[c.key])}
                            sx={
                              hierarchyRows
                                ? { textAlign: 'left', justifyContent: 'flex-start', lineHeight: 1.4 }
                                : undefined
                            }
                            onClick={(e) => onSelect(r, e.ctrlKey || e.metaKey || e.shiftKey)}
                          >
                            {text(r[c.key])}
                          </Button>
                        </Stack>
                      ) : dataBars && c.numeric ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 64 }}>
                          <Box
                            aria-hidden="true"
                            sx={{
                              width: `${Math.max(0, Math.min(1, (number(r[c.key]) ?? 0) / maxima[c.key])) * 45}%`,
                              height: 22,
                              flexShrink: 0,
                              bgcolor: surfaces.bar,
                            }}
                          />
                          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                            {format(r[c.key], c.percent)}
                          </Box>
                        </Box>
                      ) : c.numeric ? (
                        format(r[c.key], c.percent)
                      ) : (
                        text(r[c.key])
                      )}
                    </TableCell>
                  ))}
                </>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={safePage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
        onPageChange={(_, p) => setPage(p)}
      />
      <Typography variant="caption" title={source} sx={{ display: 'block', px: 2, pb: 2, overflowWrap: 'anywhere' }}>
        {source.includes('· snapshot') ? `Snapshot ${source.split('· snapshot')[1]}` : source} · Full CSV retains every
        row and numeric precision.
      </Typography>
    </Paper>
  );
}
